import {
  AuthSession,
  AuthSessionApiDto,
  authTokensToAuthSession,
  isAccessTokenAboutToExpire,
  toAuthSession,
} from "@/model/AuthSession";
import {
  BloodGlucoseObservationsDto,
  toBloodGlucoseObservations,
} from "@/model/BloodGlucoseObservations";
import {
  BloodPressureObservationsDto,
  toBloodPressureObservations,
} from "@/model/BloodPressureObservations";
import { CaregiverApiDto, toCaregiver } from "@/model/Caregiver";
import { ClinicalNoteApiDto, CreateClinicalNoteApiDto, toClinicalNote } from "@/model/ClinicalNote";
import {
  PersonalizedDiabetesProtocolApiDto,
  PersonalizedHypertensionProtocolApiDto,
} from "@/model/ClinicalProtocol";
import { LoginCredentials } from "@/model/LoginCredentials";
import {
  isMfaChallengeResponse,
  LoginResponse,
  LoginResult,
  MfaChallenge,
  MfaChallengeApiDto,
  toMfaChallenge,
} from "@/model/LoginResponse";
import { MedicationApiDto } from "@/model/Medication";
import { OrganizationApiDto } from "@/model/Organization";
import { PatientOffboardingReason } from "@/model/Patient";
import { PatientApiDto } from "@/model/PatientApiDto";
import { Instant } from "@js-joda/core";
import merge from "lodash/merge";
import { authMode } from "./authMode";
import { authSession, authTokens, clearAuthSession, setAuthSession, setAuthTokens } from "./store";

const backendUrl = import.meta.env.VITE_BACKEND_API_URL as string;
const appVersion = import.meta.env.VITE_APP_VERSION as string;
// In cookie mode the care team auth app owns the session: it signs the user in, and its API refreshes and
// revokes the cookies, which it shares with this portal under its domain.
const authAppUrl = import.meta.env.VITE_AUTH_APP_URL as string;
const authApiUrl =
  authMode === "cookie" ? (import.meta.env.VITE_AUTH_API_URL as string) : backendUrl;

export const redirectToAuthAppLogin = () => window.location.assign(`${authAppUrl}/login`);

export class HttpError extends Error {
  statusCode: number;
  responseJson?: unknown;

  constructor(statusCode: number, url: string, responseJson?: unknown) {
    super(`HttpError: ${statusCode} ${url} ${JSON.stringify(responseJson)}`);
    this.statusCode = statusCode;
    this.responseJson = responseJson;
  }
}

export const jsonFetch = async <T>(url: string, options: RequestInit = {}) => {
  const defaultOptions: RequestInit = {
    ...(authMode === "cookie" ? { credentials: "include" as const } : {}),
    headers: {
      "Content-Type": "application/json",
      "X-App-Version": appVersion,
    },
  };

  const response = await fetch(url, merge(defaultOptions, options));
  const responseBody = await response.text();
  const responseJson = responseBody ? JSON.parse(responseBody) : undefined;

  if (!response.ok) {
    throw new HttpError(response.status, url, responseJson);
  }

  return responseJson as T;
};

export const authenticatedFetch = async <T>(url: string, options: RequestInit = {}) => {
  if (isAccessTokenAboutToExpire(authSession()!)) {
    await refreshSession();
  }
  try {
    return await jsonFetch<T>(url, withAuthorization(options));
  } catch (error) {
    if (error instanceof HttpError && error.statusCode === 401) {
      await refreshSession();
      return await jsonFetch<T>(url, withAuthorization(options));
    }
    throw error;
  }
};

const withAuthorization = (options: RequestInit): RequestInit => {
  if (authMode === "cookie") {
    return options;
  }
  return merge({ headers: { Authorization: `Bearer ${authTokens()!.accessJwt.token}` } }, options);
};

const storeSession = (response: AuthSessionApiDto | LoginResponse): AuthSession => {
  if (authMode === "cookie") {
    const session = response as AuthSessionApiDto;
    setAuthSession(session);
    return toAuthSession(session);
  }
  const tokens = response as LoginResponse;
  setAuthTokens(tokens);
  return authTokensToAuthSession(tokens);
};

const startSession = async (url: string, body: unknown): Promise<LoginResult> => {
  const response = await jsonFetch<AuthSessionApiDto | LoginResponse | MfaChallengeApiDto>(url, {
    method: "POST",
    body: JSON.stringify(body),
  });
  if (isMfaChallengeResponse(response)) {
    return { type: "mfa-challenge", challenge: toMfaChallenge(response) };
  }
  return { type: "authenticated", session: storeSession(response) };
};

export const logInWithEmailAndPassword = ({ email, password }: LoginCredentials) =>
  startSession(`${backendUrl}/v1/login`, { email, password });

export type LogInWithMfaCodeParams = {
  challenge: MfaChallenge;
  code: string;
};

export const logInWithMfaCode = async ({ challenge, code }: LogInWithMfaCodeParams) => {
  const response = await jsonFetch<AuthSessionApiDto | LoginResponse>(
    `${backendUrl}/v1/login/mfa`,
    {
      method: "POST",
      body: JSON.stringify({
        challenge: challenge.method,
        userId: challenge.userId,
        session: challenge.session,
        code,
      }),
    }
  );
  return storeSession(response);
};

export const logOut = async () => {
  await jsonFetch(`${authApiUrl}/v1/logout`, { method: "POST", body: logOutBody() }).catch(
    () => {}
  );
  clearAuthSession();
};

// Cookie mode only: a session started by the auth app is restored from the refresh cookie it shares with this portal.
let restoringSessionPromise: Promise<boolean> | null = null;

export const restoreSession = (): Promise<boolean> => {
  if (!restoringSessionPromise) {
    restoringSessionPromise = jsonFetch<AuthSessionApiDto>(`${authApiUrl}/v1/token/refresh`, {
      method: "POST",
    })
      .then((session) => {
        setAuthSession(session);
        return true;
      })
      .catch(() => false)
      .finally(() => {
        restoringSessionPromise = null;
      });
  }
  return restoringSessionPromise;
};

const logOutBody = () =>
  authMode === "bearer"
    ? JSON.stringify({ refreshToken: authTokens()!.refreshJwt.token })
    : undefined;

let refreshingSessionPromise: Promise<void> | null = null;

const refreshSession = async () => {
  const actuallyRefreshSession = async () => {
    try {
      await requestSessionRefresh();
    } catch (error) {
      if (error instanceof HttpError && (error.statusCode === 401 || error.statusCode === 403)) {
        clearAuthSession(); // This will trigger a redirect to the login page
      }
      throw error;
    }
  };

  if (!refreshingSessionPromise) {
    refreshingSessionPromise = actuallyRefreshSession().finally(() => {
      refreshingSessionPromise = null;
    });
  }

  return refreshingSessionPromise;
};

const throwIfSignedOut = (url: string) => {
  if (!authSession()) {
    throw new HttpError(401, url, { reason: "stale-refresh" });
  }
};

const requestSessionRefresh = async () => {
  const url = `${authApiUrl}/v1/token/refresh`;
  if (authMode === "cookie") {
    const session = await jsonFetch<AuthSessionApiDto>(url, { method: "POST" });
    throwIfSignedOut(url);
    setAuthSession(session);
    return;
  }
  const current = authTokens()!;
  const tokens = await jsonFetch<LoginResponse>(url, {
    method: "POST",
    body: JSON.stringify({ refreshToken: current.refreshJwt.token }),
  });
  throwIfSignedOut(url);
  setAuthTokens(tokens);
};

export type ResendInvitationEmailParams = {
  email: string;
};

export const resendInvitationEmail = ({ email }: ResendInvitationEmailParams) =>
  jsonFetch(`${backendUrl}/v1/invitation/resend`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export type CheckSignUpInvitationParams = {
  token: string;
  userId: string;
};

export const checkSignUpInvitation = async ({ token, userId }: CheckSignUpInvitationParams) => {
  await jsonFetch(`${backendUrl}/v1/invitation/check`, {
    method: "POST",
    body: JSON.stringify({ token, userId }),
  });
};

export type SignUpParams = {
  token: string;
  userId: string;
  password: string;
};

export const signUp = ({ token, userId, password }: SignUpParams) =>
  startSession(`${backendUrl}/v1/invitation/accept`, { token, userId, password });

export type SendForgotPasswordEmailParams = {
  email: string;
};

export const sendForgotPasswordEmail = ({ email }: SendForgotPasswordEmailParams) =>
  jsonFetch(`${backendUrl}/v1/password/forgot`, {
    method: "POST",
    body: JSON.stringify({ email }),
  });

export type CheckResetPasswordTokenParams = {
  token: string;
  userId: string;
};

export const checkResetPasswordToken = async ({ token, userId }: CheckResetPasswordTokenParams) => {
  await jsonFetch(`${backendUrl}/v1/password/reset/check`, {
    method: "POST",
    body: JSON.stringify({ token, userId }),
  });
};

export type ResetPasswordParams = {
  token: string;
  userId: string;
  password: string;
};

export const resetPassword = ({ token, userId, password }: ResetPasswordParams) =>
  startSession(`${backendUrl}/v1/password/reset`, { token, userId, password });

export const getSelfUser = async () => {
  const dto = await authenticatedFetch<CaregiverApiDto>(`${backendUrl}/v1/self`);
  return toCaregiver(dto);
};

export const getOrganization = (organizationId: string) =>
  authenticatedFetch<OrganizationApiDto>(`${backendUrl}/v1/organizations/${organizationId}`);

export const getPatientsForOrganization = async (organizationId: string) =>
  await authenticatedFetch<PatientApiDto[]>(
    `${backendUrl}/v1/organizations/${organizationId}/patients`
  );

export type GetPatientOptions = {
  patientId: string;
  organizationId: string;
};

export const getPatient = async ({ patientId, organizationId }: GetPatientOptions) =>
  await authenticatedFetch<PatientApiDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}`
  );

export type SetPatientEducationOptions = {
  prescriptionId: string;
  educationProvided: boolean;
};

export const setPatientEducation = async ({
  prescriptionId,
  educationProvided,
}: SetPatientEducationOptions) => {
  await authenticatedFetch(`${backendUrl}/v1/prescriptions/${prescriptionId}/education`, {
    method: "PUT",
    body: JSON.stringify({ educationProvided }),
  });
};

export type SetStickyNoteOptions = {
  prescriptionId: string;
  stickyNote: string;
};

export const setStickyNote = async ({ prescriptionId, stickyNote }: SetStickyNoteOptions) => {
  await authenticatedFetch(`${backendUrl}/v1/prescriptions/${prescriptionId}/sticky-note`, {
    method: "PUT",
    body: JSON.stringify({ stickyNote }),
  });
};

export type GetClinicalNotesOptions = {
  patientId: string;
  organizationId: string;
};

export const getClinicalNotes = async ({ patientId, organizationId }: GetClinicalNotesOptions) => {
  const dtos = await authenticatedFetch<ClinicalNoteApiDto[]>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/clinical-notes`
  );
  return dtos.map((dto) => toClinicalNote(dto));
};

export type AddClinicalNoteOptions = {
  patientId: string;
  organizationId: string;
  note: CreateClinicalNoteApiDto;
};

export const addClinicalNote = ({ patientId, organizationId, note }: AddClinicalNoteOptions) =>
  authenticatedFetch<ClinicalNoteApiDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/clinical-notes`,
    {
      method: "POST",
      body: JSON.stringify(note),
    }
  );

export type UpdateDiabetesMedicationsOptions = {
  patientId: string;
  organizationId: string;
  medications: MedicationApiDto[];
};

export const updateDiabetesMedications = async ({
  organizationId,
  patientId,
  medications,
}: UpdateDiabetesMedicationsOptions) => {
  const body = medications.map((medication) => ({ ...medication, unit: "" }));
  await authenticatedFetch(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/medications/diabetes`,
    { method: "PUT", body: JSON.stringify(body) }
  );
};

export type UpdatePersonalizedDiabetesProtocolOptions = {
  prescriptionId: string;
  personalizedProtocol: PersonalizedDiabetesProtocolApiDto;
};

export const updatePersonalizedDiabetesProtocol = async ({
  prescriptionId,
  personalizedProtocol,
}: UpdatePersonalizedDiabetesProtocolOptions) => {
  await authenticatedFetch(
    `${backendUrl}/v1/prescriptions/${prescriptionId}/clinical-protocol/diabetes`,
    {
      method: "PUT",
      body: JSON.stringify(personalizedProtocol),
    }
  );
};

export type UpdatePersonalizedHypertensionProtocolOptions = {
  prescriptionId: string;
  personalizedProtocol: PersonalizedHypertensionProtocolApiDto;
};

export const updatePersonalizedHypertensionProtocol = async ({
  prescriptionId,
  personalizedProtocol,
}: UpdatePersonalizedHypertensionProtocolOptions) => {
  await authenticatedFetch(
    `${backendUrl}/v1/prescriptions/${prescriptionId}/clinical-protocol/hypertension`,
    {
      method: "PUT",
      body: JSON.stringify(personalizedProtocol),
    }
  );
};

export type GetObservationsOptions = {
  patientId: string;
  organizationId: string;
  from: Instant;
  to: Instant;
};

export const getBloodGlucoseObservations = async ({
  patientId,
  organizationId,
  from,
  to,
}: GetObservationsOptions) => {
  const queryParams = new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  });
  const dto = await authenticatedFetch<BloodGlucoseObservationsDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/observations/blood-glucose?${queryParams}`
  );
  return toBloodGlucoseObservations(dto);
};

export const getBloodPressureObservations = async ({
  patientId,
  organizationId,
  from,
  to,
}: GetObservationsOptions) => {
  const queryParams = new URLSearchParams({
    from: from.toString(),
    to: to.toString(),
  });
  const dto = await authenticatedFetch<BloodPressureObservationsDto>(
    `${backendUrl}/v1/organizations/${organizationId}/patients/${patientId}/observations/blood-pressure?${queryParams}`
  );
  return toBloodPressureObservations(dto);
};

export type OffboardPatientOptions = {
  prescriptionId: string;
  reason: PatientOffboardingReason;
  deliveryDate?: string;
  note?: string;
};

export const offboardPatient = async ({
  prescriptionId,
  reason,
  deliveryDate,
  note,
}: OffboardPatientOptions) => {
  await authenticatedFetch(`${backendUrl}/v1/prescriptions/${prescriptionId}/offboard`, {
    method: "POST",
    body: JSON.stringify({
      reason,
      deliveryDate,
      note,
    }),
  });
};

export type UpdateDeliveryDateOptions = {
  patientId: string;
  deliveryDate: string | undefined;
};

export const updateDeliveryDate = async ({
  patientId,
  deliveryDate,
}: UpdateDeliveryDateOptions) => {
  await authenticatedFetch(`${backendUrl}/v1/patients/${patientId}/delivery-date`, {
    method: "PUT",
    body: JSON.stringify({
      deliveryDate,
    }),
  });
};

export type GetPrescribingCaregiversOptions = {
  organizationId: string;
};

export const getPrescribingCaregivers = async ({
  organizationId,
}: GetPrescribingCaregiversOptions) => {
  const dtos = await authenticatedFetch<CaregiverApiDto[]>(
    `${backendUrl}/v1/organizations/${organizationId}/caregivers`
  );
  return dtos.map((dto) => toCaregiver(dto));
};
